(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const initReveal = () => {
    const items = Array.from(document.querySelectorAll("[data-reveal]"));
    if (reducedMotion || !("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-revealed"));
      return;
    }

    document.documentElement.classList.add("reveal-ready");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.06, rootMargin: "0px 0px -5% 0px" },
    );
    items.forEach((item) => observer.observe(item));
  };

  const initCountUp = () => {
    const items = Array.from(document.querySelectorAll("[data-count-target]"));
    if (reducedMotion || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.textContent = `${item.dataset.countTarget}%`;
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const item = entry.target;
          observer.unobserve(item);
          const target = Number(item.dataset.countTarget);
          const start = Math.min(Math.max(Number(item.dataset.countStart), 0), target - 1);
          const startedAt = performance.now();
          const duration = 1500;

          const update = (timestamp) => {
            const progress = Math.min((timestamp - startedAt) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            item.textContent = `${Math.round(start + (target - start) * eased)}%`;
            if (progress < 1) window.requestAnimationFrame(update);
          };
          window.requestAnimationFrame(update);
        });
      },
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" },
    );

    items.forEach((item) => {
      item.textContent = `${item.dataset.countStart}%`;
      observer.observe(item);
    });
  };

  const initCurriculumMap = () => {
    const section = document.querySelector("[data-curriculum-map]");
    const route = section?.querySelector(".curriculum-route-line");
    const path = section?.querySelector(".curriculum-path");
    const nodes = section ? Array.from(section.querySelectorAll(".curriculum-node")) : [];
    if (!section || !route || !path || !nodes.length) return;

    const routeLength = route.getTotalLength();
    const routeGap = routeLength + 1;
    route.style.strokeDasharray = `0 ${routeGap}`;
    route.style.strokeDashoffset = "0";

    let maximumProgress = 0;
    let revealed = false;
    let scrollRunwayReleased = false;
    let frame = null;

    const revealAll = () => {
      maximumProgress = 1;
      revealed = true;
      route.style.strokeDasharray = `${routeLength} ${routeGap}`;
      route.style.strokeDashoffset = "0";
      path.style.setProperty("--curriculum-mobile-progress", "100%");
      nodes.forEach((node) => {
        node.classList.add("is-number-visible", "is-copy-visible");
      });
    };

    const releaseScrollRunway = () => {
      revealAll();
      scrollRunwayReleased = true;
      section.classList.add("is-scroll-complete");
    };

    section.addEventListener("curriculum:complete", releaseScrollRunway);

    const viewModulesLink = document.querySelector("[data-view-modules]");
    viewModulesLink?.addEventListener("click", (event) => {
      event.preventDefault();
      releaseScrollRunway();
      window.requestAnimationFrame(() => {
        const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY);
        window.scrollTo({
          top,
          behavior: reducedMotion ? "auto" : "smooth",
        });
      });
    });

    if (reducedMotion) {
      releaseScrollRunway();
      return;
    }

    document.documentElement.classList.add("curriculum-scroll-ready");

    const numberThresholds = [0, 0.16, 0.34, 0.52, 0.72, 0.92];
    const copyThresholds = [0, 0.2, 0.38, 0.56, 0.76, 0.94];

    const update = () => {
      const rect = section.getBoundingClientRect();
      const desktop = window.matchMedia("(min-width: 821px)").matches;
      let progress;

      if (desktop) {
        const travel = Math.max(rect.height - window.innerHeight, 1);
        const revealTravel = Math.max(travel * 0.78, 1);
        progress = Math.min(Math.max(-rect.top / revealTravel, 0), 1);
      } else {
        const travel = Math.max(rect.height - window.innerHeight * 0.25, 1);
        progress = Math.min(Math.max(-rect.top / travel, 0), 1);
      }

      maximumProgress = Math.max(maximumProgress, progress);
      const routeProgress = Math.min(1, 0.065 + maximumProgress * 1.035);
      route.style.strokeDasharray = `${routeLength * routeProgress} ${routeGap}`;
      path.style.setProperty("--curriculum-mobile-progress", routeProgress * 100 + "%");

      nodes.forEach((node, index) => {
        if (maximumProgress >= numberThresholds[index]) {
          node.classList.add("is-number-visible");
          if (index === nodes.length - 1) {
            route.style.strokeDasharray = `${routeLength} ${routeGap}`;
          }
        }
        if (maximumProgress >= copyThresholds[index]) {
          node.classList.add("is-copy-visible");
        }
      });

      if (desktop && !revealed && maximumProgress >= 0.995) {
        revealAll();
      }

      if (desktop && revealed && !scrollRunwayReleased && rect.bottom <= 0) {
        releaseScrollRunway();
      }

      frame = null;
    };

    const requestUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  };

  const initTeamPricing = () => {
    const calculators = Array.from(document.querySelectorAll("[data-team-calculator]"));
    const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

    calculators.forEach((calculator) => {
      const toggle = calculator.querySelector("[data-team-calculator-toggle]");
      const panel = calculator.querySelector("[data-team-calculator-panel]");
      const input = calculator.querySelector("[data-team-size-input]");
      const total = calculator.querySelector("[data-team-total]");
      const selectedSize = calculator.querySelector("[data-team-selected-size]");
      const effectiveRate = calculator.querySelector("[data-team-effective-rate]");
      const accessSummary = calculator.querySelector("[data-team-access-summary]");
      const pricePrimary = calculator.closest(".pricing-option")?.querySelector("[data-team-price-primary]");
      const priceUnit = calculator.closest(".pricing-option")?.querySelector("[data-team-price-unit]");
      const standardResult = calculator.querySelector("[data-team-standard-result]");
      const customResult = calculator.querySelector("[data-team-custom-result]");
      const priceResult = calculator.querySelector("[data-team-price-result]");
      const chips = Array.from(calculator.querySelectorAll("[data-team-size]"));
      const customTrigger = calculator.querySelector("[data-team-custom-trigger]");
      const checkoutForm = calculator.querySelector("[data-team-checkout-form]");
      const variantInput = checkoutForm?.querySelector("[data-team-variant-input]");
      const seatsProperty = checkoutForm?.querySelector("[data-team-seats-property]");
      const pricingProperty = checkoutForm?.querySelector("[data-team-pricing-property]");
      const variantMapElement = calculator.closest(".pricing-option")?.querySelector("[data-team-variant-map]");
      let teamVariants = {};

      try {
        teamVariants = variantMapElement ? JSON.parse(variantMapElement.textContent) : {};
      } catch (error) {
        teamVariants = {};
      }

      if (!toggle || !panel || !input || !total || !selectedSize || !effectiveRate || !accessSummary || !pricePrimary) return;

      const clamp = (value) => Math.min(29, Math.max(2, Number.parseInt(value, 10) || 2));
      const format = (value) => "$" + money.format(value);

      const focusExpandedCalculator = () => {
        const desktop = window.matchMedia("(min-width: 821px)").matches;
        const target = desktop ? calculator.closest(".pricing-grid") : calculator.closest(".pricing-option-organization");
        if (!target) return;

        const header = document.querySelector(".header-shell");
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY + (desktop ? 0 : -16));
        header?.classList.add("is-hidden");
        window.scrollTo({ top, behavior: reducedMotion ? "auto" : "smooth" });
        window.setTimeout(() => header?.classList.add("is-hidden"), 520);
      };

      const setCustom = (custom) => {
        if (standardResult) standardResult.hidden = custom;
        if (customResult) customResult.hidden = !custom;
        if (priceResult) priceResult.hidden = custom;
        customTrigger?.setAttribute("aria-pressed", String(custom));
        chips.forEach((chip) => chip.classList.toggle("is-active", false));

        if (custom) {
          pricePrimary.textContent = "30+ people";
          if (priceUnit) priceUnit.hidden = true;
        }
      };

      const updatePrice = (rawSize) => {
        const size = clamp(rawSize);
        const calculatedTotal = size < 10
          ? size * 595
          : size < 20
            ? 5500 + ((size - 10) * 395)
            : 9250 + ((size - 20) * 395);
        const rate = Math.round(calculatedTotal / size);
        const formattedTotal = format(calculatedTotal) + " USD";

        input.value = String(size);
        setCustom(false);
        pricePrimary.textContent = size + " people";
        if (priceUnit) priceUnit.hidden = true;
        selectedSize.textContent = size + " people";
        total.textContent = formattedTotal;
        effectiveRate.textContent = "≈ " + format(rate) + " per person";
        accessSummary.textContent = "Full program access";

        if (variantInput && teamVariants[size]) variantInput.value = teamVariants[size];
        if (seatsProperty) seatsProperty.value = size + " people";
        if (pricingProperty) pricingProperty.value = formattedTotal + " · ≈ " + format(rate) + " per person";
        chips.forEach((chip) => chip.classList.toggle("is-active", Number(chip.dataset.teamSize) === size));
      };

      const setExpanded = (expanded) => {
        toggle.setAttribute("aria-expanded", String(expanded));
        calculator.closest(".pricing-option")?.classList.toggle("is-team-calculator-expanded", expanded);

        if (!expanded) {
          setCustom(false);
          pricePrimary.textContent = "From ≈ $440 USD";
          if (priceUnit) priceUnit.hidden = false;
        } else {
          updatePrice(input.value);
        }

        if (reducedMotion) {
          panel.hidden = !expanded;
          panel.style.removeProperty("height");
          if (expanded) focusExpandedCalculator();
          return;
        }

        if (expanded) {
          panel.hidden = false;
          panel.style.height = "0px";
          window.requestAnimationFrame(() => {
            panel.style.height = panel.scrollHeight + "px";
          });
          panel.addEventListener("transitionend", () => {
            if (toggle.getAttribute("aria-expanded") === "true") {
              panel.style.height = "auto";
              focusExpandedCalculator();
            }
          }, { once: true });
        } else {
          const finishClose = () => {
            if (toggle.getAttribute("aria-expanded") === "false") {
              panel.hidden = true;
              panel.style.removeProperty("height");
            }
          };
          panel.style.height = panel.scrollHeight + "px";
          panel.getBoundingClientRect();
          window.requestAnimationFrame(() => { panel.style.height = "0px"; });
          panel.addEventListener("transitionend", finishClose, { once: true });
          window.setTimeout(finishClose, 380);
        }
      };

      toggle.addEventListener("click", () => {
        setExpanded(toggle.getAttribute("aria-expanded") !== "true");
      });

      calculator.querySelectorAll("[data-team-step]").forEach((button) => {
        button.addEventListener("click", () => {
          const delta = button.dataset.teamStep === "plus" ? 1 : -1;
          updatePrice(clamp(input.value) + delta);
        });
      });

      input.addEventListener("input", () => {
        const value = Number.parseInt(input.value, 10);
        if (Number.isFinite(value) && value >= 2 && value <= 29) updatePrice(value);
      });
      input.addEventListener("change", () => updatePrice(input.value));
      chips.forEach((chip) => chip.addEventListener("click", () => updatePrice(chip.dataset.teamSize)));
      customTrigger?.addEventListener("click", () => setCustom(true));
    });
  };


const initDirectCheckout = () => {
    const forms = Array.from(document.querySelectorAll(".enroll-checkout-form, [data-team-checkout-form]"));

    forms.forEach((form) => {
      form.addEventListener("submit", async (event) => {
        if (form.dataset.checkoutSubmitting === "true") return;
        event.preventDefault();

        const submit = form.querySelector('button[type="submit"]');
        const originalLabel = submit?.textContent;
        form.dataset.checkoutSubmitting = "true";
        if (submit) {
          submit.disabled = true;
          submit.textContent = "Opening cart…";
        }

        try {
          const formData = new FormData(form);
          const properties = {};
          formData.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          });

          const clearResponse = await fetch("/cart/clear.js", {
            method: "POST",
            headers: { Accept: "application/json" },
          });
          if (!clearResponse.ok) throw new Error("Unable to reset cart.");

          const addResponse = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: [{
                id: Number(formData.get("id")),
                quantity: Number(formData.get("quantity")) || 1,
                properties,
              }],
            }),
          });
          if (!addResponse.ok) throw new Error("Unable to add selection.");

          window.location.assign("/cart");
        } catch (error) {
          form.dataset.checkoutSubmitting = "false";
          if (submit) {
            submit.disabled = false;
            submit.textContent = originalLabel;
          }
          window.location.assign("/cart");
        }
      });
    });
  };

  const initInternalLinks = () => {
    const prepareAnchor = (hash) => {
      if (hash === "#program-access") {
        document.querySelector("[data-curriculum-map]")?.dispatchEvent(new Event("curriculum:complete"));
        document.querySelector(".header-shell")?.classList.add("is-hidden");
      }
    };

    const scrollToTarget = (target, behavior) => {
      const scrollMargin = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
      window.scrollTo({
        top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - scrollMargin),
        behavior,
      });
    };

    const navigateToHash = (hash, behavior) => {
      if (!hash || hash === "#") return false;
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (!target) return false;
      prepareAnchor(hash);
      const scroll = () => scrollToTarget(target, behavior);
      if (hash === "#program-access") {
        window.requestAnimationFrame(() => window.requestAnimationFrame(scroll));
      } else {
        scroll();
      }
      return true;
    };

    document.addEventListener("click", (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) return;

      const link = event.target.closest('a[href^="#"]');
      const hash = link?.getAttribute("href");
      if (!hash || hash === "#") return;
      if (!document.getElementById(decodeURIComponent(hash.slice(1)))) return;

      event.preventDefault();
      window.history.pushState(null, "", hash);
      navigateToHash(hash, reducedMotion ? "auto" : "smooth");
    });

    if (window.location.hash === "#program-access") {
      navigateToHash(window.location.hash, "auto");
    }
  };

  const initHeader = () => {
    const shell = document.querySelector(".header-shell");
    if (!shell) return;
    let lastScrollY = window.scrollY;
    let frame = null;

    window.addEventListener(
      "scroll",
      () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          const current = Math.max(window.scrollY, 0);
          const delta = current - lastScrollY;
          const menuOpen = document.querySelector(".mobile-menu.is-open");
          if (current <= 16 || menuOpen) {
            shell.classList.remove("is-hidden");
            lastScrollY = current;
          } else if (Math.abs(delta) >= 10) {
            shell.classList.toggle("is-hidden", delta > 0);
            lastScrollY = current;
          }
          frame = null;
        });
      },
      { passive: true },
    );

    shell.addEventListener("focusin", () => shell.classList.remove("is-hidden"));
  };

  const initMobileMenu = () => {
    const menu = document.querySelector(".mobile-menu");
    const button = menu?.querySelector(".menu-button");
    const panel = menu?.querySelector(".mobile-menu-panel");
    if (!menu || !button || !panel) return;

    const setOpen = (open) => {
      menu.classList.toggle("is-open", open);
      button.setAttribute("aria-expanded", String(open));
      button.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
      panel.hidden = !open;
    };

    button.addEventListener("click", () => setOpen(!menu.classList.contains("is-open")));
    panel.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => setOpen(false)));
    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.focus();
      }
    });
  };

  const initInquiryForms = () => {
    const forms = Array.from(document.querySelectorAll("[data-inquiry-form]"));

    forms.forEach((form) => {
      const errorSummary = form.querySelector(".client-form-errors");
      const requiredFields = Array.from(form.querySelectorAll("[required]"));

      const clearFieldError = (field) => {
        field.removeAttribute("aria-invalid");
      };

      requiredFields.forEach((field) => {
        field.addEventListener("input", () => clearFieldError(field));
        field.addEventListener("change", () => clearFieldError(field));
      });

      form.addEventListener("submit", (event) => {
        const invalidFields = requiredFields.filter((field) => !field.checkValidity());
        if (!invalidFields.length) return;

        event.preventDefault();
        invalidFields.forEach((field) => field.setAttribute("aria-invalid", "true"));

        if (errorSummary) {
          errorSummary.textContent =
            invalidFields.length === 1
              ? "Please complete the highlighted field."
              : "Please complete the highlighted fields.";
          errorSummary.hidden = false;
          errorSummary.focus();
        } else {
          invalidFields[0].focus();
        }
      });
    });

    const serverStatus = document.querySelector("[data-form-status]");
    if (serverStatus) serverStatus.focus();
  };



  const initMarketingConsentForms = () => {
    const signupForm = document.querySelector('form[data-marketing-signup]');
    if (!signupForm) return;

    const signupEmail = signupForm.querySelector('input[name="contact[email]"]');
    if (!signupEmail) return;

    document.querySelectorAll('form[data-marketing-consent-form]').forEach((form) => {
      const consent = form.querySelector('[data-marketing-consent]');
      const email = form.querySelector('input[name="contact[email]"]');
      const error = form.querySelector('.marketing-consent-error');
      const submitButton = form.querySelector('button[type="submit"]');
      if (!consent || !email || !error || !submitButton) return;

      form.addEventListener('submit', async (event) => {
        if (form.dataset.marketingSubmitting === 'true') {
          event.preventDefault();
          return;
        }
        if (!consent.checked) return;
        if (!form.checkValidity()) {
          event.preventDefault();
          form.reportValidity();
          return;
        }

        event.preventDefault();
        form.dataset.marketingSubmitting = 'true';
        submitButton.disabled = true;
        error.hidden = true;
        signupEmail.value = email.value.trim();

        try {
          const response = await fetch(signupForm.action, {
            method: 'POST',
            body: new FormData(signupForm),
            credentials: 'same-origin',
            redirect: 'follow',
            headers: { Accept: 'text/html' },
          });
          const responsePage = await response.text();
          const responseUrl = new URL(response.url, window.location.href);
          const signupSucceeded = responseUrl.searchParams.get('customer_posted') === 'true' ||
            responsePage.includes('data-marketing-signup-success');
          if (!response.ok || !signupSucceeded) throw new Error('Signup could not be confirmed');

          HTMLFormElement.prototype.submit.call(form);
        } catch (_) {
          error.textContent = 'We could not confirm your email-updates signup. Please try again, or uncheck the box to send your inquiry without subscribing.';
          error.hidden = false;
          error.focus();
          submitButton.disabled = false;
          form.dataset.marketingSubmitting = 'false';
        }
      });
    });
  };

  const initLegalNavigation = () => {
    const content = document.querySelector('.legal-content');
    const tocLinks = Array.from(document.querySelectorAll('[data-legal-toc] a[href^="#"]'));
    const mobileToc = document.querySelector('.legal-mobile-toc');
    const sections = content ? Array.from(content.querySelectorAll('[data-legal-section][id]')) : [];
    if (!content || !tocLinks.length || !sections.length) return;

    const setActive = (id) => {
      tocLinks.forEach((link) => {
        const active = link.getAttribute('href') === '#' + id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const selectFromViewport = () => {
      const viewportLine = window.innerHeight * 0.36;
      let active = sections[0];
      sections.forEach((section) => {
        if (section.getBoundingClientRect().top <= viewportLine) active = section;
      });
      setActive(active.id);
    };

    if (reducedMotion || !('IntersectionObserver' in window)) {
      selectFromViewport();
      window.addEventListener('scroll', selectFromViewport, { passive: true });
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setActive(visible[0].target.id);
        },
        { rootMargin: '-22% 0px -60% 0px', threshold: 0.01 },
      );
      sections.forEach((section) => observer.observe(section));
      selectFromViewport();
    }

    mobileToc?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        mobileToc.open = false;
      });
    });
  };

  const init = () => {
    initReveal();
    initCountUp();
    initCurriculumMap();
    initTeamPricing();
    initDirectCheckout();
    initInternalLinks();
    initHeader();
    initMobileMenu();
    initInquiryForms();
    initMarketingConsentForms();
    initLegalNavigation();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
